import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["recordPickers", "previewOutput", "testResult"];
  static values = {
    recordsUrl: String,
    previewUrl: String,
    testUrl: String,
    searchRecordsUrl: String,
    kind: String,
  };

  declare recordsUrlValue: string;
  declare previewUrlValue: string;
  declare testUrlValue: string;
  declare searchRecordsUrlValue: string;
  declare kindValue: string;
  declare hasRecordPickersTarget: boolean;
  declare recordPickersTarget: HTMLElement;
  declare hasPreviewOutputTarget: boolean;
  declare previewOutputTarget: HTMLElement;
  declare hasTestResultTarget: boolean;
  declare testResultTarget: HTMLElement;

  private availableRecords: Array<{ value: string; label: string }> = [];

  connect() {
    this.loadAvailableRecords();
  }

  async loadAvailableRecords() {
    if (!this.recordsUrlValue) return;
    try {
      const response = await fetch(this.recordsUrlValue, {
        headers: { Accept: "application/json" },
      });
      const data = await response.json();
      this.availableRecords = [
        ...(data.event || []),
        ...(data.primary || []),
        ...(data.profile || []),
      ];
      this.buildRecordPickers();
    } catch {
      // silently fail
    }
  }

  buildRecordPickers() {
    if (!this.hasRecordPickersTarget) return;

    const pickableRecords = this.availableRecords.filter(
      (r) => r.value !== "user_profile"
    );
    if (pickableRecords.length === 0) return;

    pickableRecords.forEach((record) => {
      const group = document.createElement("div");
      group.className = "form-group row";

      const labelCol = document.createElement("div");
      labelCol.className = "col-4";
      labelCol.innerHTML = `<label class="col-form-label">${this.escapeHtml(record.label)}</label>`;

      const inputCol = document.createElement("div");
      inputCol.className = "col-8";

      const select = document.createElement("select");
      select.className = "form-control";
      select.setAttribute("data-preview-record", record.value);

      const blankOption = document.createElement("option");
      blankOption.value = "";
      blankOption.textContent = `-- Select ${record.label} --`;
      select.appendChild(blankOption);

      inputCol.appendChild(select);
      group.appendChild(labelCol);
      group.appendChild(inputCol);
      this.recordPickersTarget.appendChild(group);

      this.loadSelectOptions(select, record.value);
    });
  }

  private async loadSelectOptions(
    select: HTMLSelectElement,
    recordType: string
  ) {
    if (!this.searchRecordsUrlValue) return;

    try {
      const url = `${this.searchRecordsUrlValue}?record_type=${encodeURIComponent(recordType)}&q=`;
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      const data = await response.json();

      (data.items || []).forEach((item: { id: number; text: string }) => {
        const option = document.createElement("option");
        option.value = String(item.id);
        option.textContent = item.text;
        select.appendChild(option);
      });
    } catch {
      // silently fail
    }
  }

  private collectRecordIds(): Record<string, string> {
    const records: Record<string, string> = {};
    this.element
      .querySelectorAll<HTMLSelectElement | HTMLInputElement>(
        "[data-preview-record]"
      )
      .forEach((el) => {
        const name = el.dataset.previewRecord;
        if (name && el.value) {
          records[name] = el.value;
        }
      });
    return records;
  }

  private csrfToken(): string | null {
    return (
      document
        .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.getAttribute("content") || null
    );
  }

  private async postJson(url: string): Promise<any> {
    const token = this.csrfToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { "X-CSRF-Token": token } : {}),
      },
      body: JSON.stringify({ records: this.collectRecordIds() }),
    });
    return response.json();
  }

  async preview() {
    if (!this.previewUrlValue || !this.hasPreviewOutputTarget) return;

    this.previewOutputTarget.innerHTML =
      '<p class="text-muted">Loading...</p>';

    try {
      const data = await this.postJson(this.previewUrlValue);

      if (data.error) {
        this.previewOutputTarget.innerHTML = `
          <div class="alert alert-danger">${data.error}</div>
        `;
        return;
      }

      if (this.kindValue === "email") {
        this.previewOutputTarget.innerHTML = `
          <h6>Subject</h6>
          <div class="bg-light p-3 rounded mb-3">${this.escapeHtml(data.subject)}</div>
          <h6>Body</h6>
          <div class="border p-3 rounded">${data.body}</div>
        `;
      } else {
        this.previewOutputTarget.innerHTML = `
          <h6>Message</h6>
          <div class="bg-light p-3 rounded">${this.escapeHtml(data.message)}</div>
          <p class="text-muted mt-2">Recipient: ${this.escapeHtml(data.recipient || "N/A")}</p>
        `;
      }
    } catch (e) {
      this.previewOutputTarget.innerHTML = `
        <div class="alert alert-danger">Failed to generate preview: ${e instanceof Error ? e.message : "Unknown error"}</div>
      `;
    }
  }

  async sendTest() {
    if (!this.testUrlValue || !this.hasTestResultTarget) return;

    this.testResultTarget.innerHTML =
      '<p class="text-muted">Sending...</p>';

    try {
      const data = await this.postJson(this.testUrlValue);

      if (data.error) {
        this.testResultTarget.innerHTML = `
          <div class="alert alert-danger">${data.error}</div>
        `;
        return;
      }

      if (this.kindValue === "email") {
        this.testResultTarget.innerHTML = `
          <div class="alert alert-success">Test email sent to <strong>${this.escapeHtml(data.sent_to)}</strong></div>
        `;
      } else {
        this.testResultTarget.innerHTML = `
          <div class="alert alert-success">Test SMS sent to <strong>${this.escapeHtml(data.sent_to)}</strong></div>
        `;
      }
    } catch (e) {
      this.testResultTarget.innerHTML = `
        <div class="alert alert-danger">Failed to send test: ${e instanceof Error ? e.message : "Unknown error"}</div>
      `;
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
